/**
 * Gesture State Machine
 * 
 * Single source of truth for gesture mode.
 * Prevents "pinch vs drag vs tap vs inertia" tangles.
 * 
 * Modes:
 * - idle: No active gesture
 * - panning: Single-finger drag in progress
 * - pinching: Two-finger pinch-zoom in progress  
 * - inertia: Momentum glide after pan release
 * 
 * Hard rule: Only ONE mode at a time.
 */

export const GESTURE_MODES = {
  IDLE: 'idle',
  PANNING: 'panning',
  PINCHING: 'pinching',
  INERTIA: 'inertia'
};

/**
 * Create gesture state manager
 * @returns {Object} Gesture state manager with mode and transition methods
 */
export function createGestureState() {
  let mode = GESTURE_MODES.IDLE;
  let listeners = [];

  const notify = () => {
    listeners.forEach(fn => fn(mode));
  };

  return {
    /** Get current mode */
    getMode: () => mode,

    /** Check if in specific mode */
    is: (m) => mode === m,

    /** Check if idle */
    isIdle: () => mode === GESTURE_MODES.IDLE,

    /** Check if any active gesture */
    isActive: () => mode !== GESTURE_MODES.IDLE,

    /** Transition to panning mode */
    startPan: () => {
      if (mode === GESTURE_MODES.PINCHING) return false; // Can't start pan while pinching
      mode = GESTURE_MODES.PANNING;
      notify();
      return true;
    },

    /** Transition to pinching mode (kills inertia) */
    startPinch: () => {
      mode = GESTURE_MODES.PINCHING;
      notify();
      return true;
    },

    /** Transition to inertia mode (only from panning) */
    startInertia: () => {
      if (mode !== GESTURE_MODES.PANNING) return false;
      mode = GESTURE_MODES.INERTIA;
      notify();
      return true;
    },

    /** End current gesture, return to idle */
    end: () => {
      mode = GESTURE_MODES.IDLE;
      notify();
    },

    /** Force reset to idle (emergency) */
    reset: () => {
      mode = GESTURE_MODES.IDLE;
      notify();
    },

    /** Subscribe to mode changes */
    subscribe: (fn) => {
      listeners.push(fn);
      return () => {
        listeners = listeners.filter(l => l !== fn);
      };
    }
  };
}

/**
 * Clamp offset to prevent infinite panning
 * @param {Object} offset - Current offset {x, y}
 * @param {number} maxRadius - Maximum offset radius
 * @returns {Object} Clamped offset
 */
export function clampOffset(offset, maxRadius) {
  const magnitude = Math.sqrt(offset.x * offset.x + offset.y * offset.y);
  if (magnitude <= maxRadius) return offset;
  
  const scale = maxRadius / magnitude;
  return {
    x: offset.x * scale,
    y: offset.y * scale
  };
}

/**
 * Safe scale clamping
 */
export const SCALE_MIN = 0.5;
export const SCALE_MAX = 2.0;

export function clampScale(scale) {
  return Math.min(SCALE_MAX, Math.max(SCALE_MIN, scale));
}

/**
 * Safe pinch distance check
 */
export function isPinchValid(distance) {
  return typeof distance === 'number' && 
         Number.isFinite(distance) && 
         distance > 0;
}
